import { useMutation } from '@apollo/client';
import { Button } from 'antd';
import CircularProgress from 'components/CircularProgress';
import { InputComponent } from 'components/common/Input';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import ErrorContainer from 'components/ErrorContainer';
import { Form, Formik } from 'formik';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'redux/types';
import { ActionButtonContainer } from 'routes/SignIn/components/styled';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { ResetPasswordPayload } from 'types/user';
import IntlMessages from 'utils/IntlMessages';
import * as Yup from 'yup';

const ResetPasswordSchema = Yup.object({
  password: Yup.string().min(6).required('Required'),
  rePassword: Yup.string()
    .min(6)
    .required('Required')
    .oneOf([Yup.ref('password')], 'Password mismatch'),
});

const ResetNewPassword: React.FC<{ email: string }> = ({ email }) => {
  const [updateUser, { loading }] = useMutation(UPDATE_USER);
  const { accessToken, expires } = useSelector<RootState, RootState['auth']>(({ auth }) => auth);
  const [error, setError] = useState('');
  const [isResetSuccess, setResetState] = useState(false);
  const history = useHistory();
  const initValues = { password: '', rePassword: '' };

  useEffect(() => {
    if (accessToken && expires > Date.now()) {
      history.push('/');
    }
  }, [accessToken, expires]);

  const onConfirmSuccess = () => {
    history.push('/signin');
  };

  const onFinish = async ({ password }: ResetPasswordPayload) => {
    try {
      const data = await updateUser({
        variables: {
          filter: {
            email,
          },
          updatedDoc: {
            password,
          },
        },
      });

      if (data) {
        setResetState(true);
      }
    } catch (err) {
      setError(err?.message || 'Unknown error');
    }
  };

  return (
    <>
      <div className='gx-app-login-content'>
        <Formik
          initialValues={initValues}
          name='setnewpassword'
          onSubmit={onFinish}
          validationSchema={ResetPasswordSchema}
          validateOnBlur>
          <Form className='gx-signin-form gx-form-row0'>
            <InputComponent type={INPUT_TYPE.PASSWORD} name='password' label='Password' labelType={LABEL_TYPE.above} />
            <InputComponent
              type={INPUT_TYPE.PASSWORD}
              name='rePassword'
              label='Confirm password'
              labelType={LABEL_TYPE.above}
            />
            <ActionButtonContainer marginTop='0'>
              <Button type='primary' className='gx-mb-0' htmlType='submit'>
                <IntlMessages id='app.userAuth.resetPassword' />
              </Button>
            </ActionButtonContainer>
          </Form>
        </Formik>
        <ErrorContainer>{get(error, 'message', '')}</ErrorContainer>
      </div>
      <SweetAlert
        show={isResetSuccess}
        success
        title={<IntlMessages id='resetPasswordSuccessModal.title' />}
        onConfirm={onConfirmSuccess}>
        <IntlMessages id='createAccountSuccess.content' />
      </SweetAlert>
      {loading ? (
        <div className='gx-loader-view'>
          <CircularProgress />
        </div>
      ) : null}
    </>
  );
};

export default ResetNewPassword;
